import is from '@sindresorhus/is';
import { useEffect } from 'react';

import { useActiveThreadStore } from '../ActiveThreadStoreProvider';
import {
    ANY_DESCRIPTION_ANCHOR_SELECTOR,
    COMMENT_BUTTON_ATTRIBUTE,
    COMMENT_COMPOSER_ATTRIBUTE,
    COMMENT_ID_ATTRIBUTE,
    COMMENT_RAIL_ATTRIBUTE,
    commentMarkSelector,
    SKILL_ANCHOR_SELECTOR,
    TITLE_ANCHOR_SELECTOR,
    WORKFLOW_STEP_ANCHOR_SELECTOR,
    workflowStepPartAnchorSelectorAny,
} from './grammar';

// Every mounted active-thread store installs this listener on `document`, so the selector has to
// cover every commentable surface in the app, not just the ones a given store owns: a row kind
// missing here reads as an outside click in *all* stores, dropping the group activation and any
// pending composer. Workflow-step and description-section rows were the gap.
const INSIDE_SELECTOR = [
    TITLE_ANCHOR_SELECTOR,
    SKILL_ANCHOR_SELECTOR,
    WORKFLOW_STEP_ANCHOR_SELECTOR,
    workflowStepPartAnchorSelectorAny,
    ANY_DESCRIPTION_ANCHOR_SELECTOR,
    `[${COMMENT_BUTTON_ATTRIBUTE}]`,
    `[${COMMENT_ID_ATTRIBUTE}]`,
    `[${COMMENT_COMPOSER_ATTRIBUTE}]`,
    `[${COMMENT_RAIL_ATTRIBUTE}]`,
    commentMarkSelector(),
    '[role="menu"]',
    '[role="dialog"]',
].join(',');

export const useDeactivateOnOutsideClick = (): void => {
    const setActiveThreadId = useActiveThreadStore((state) => state.setActiveThreadId);
    const cancelPending = useActiveThreadStore((state) => state.cancelPending);

    useEffect(() => {
        const handleMouseDown = (event: MouseEvent): void => {
            const target = event.target;
            if (!(target instanceof Element)) return;
            if (!is.null(target.closest(INSIDE_SELECTOR))) return;
            setActiveThreadId(null);
            cancelPending();
        };
        document.addEventListener('mousedown', handleMouseDown);
        return () => {
            document.removeEventListener('mousedown', handleMouseDown);
        };
    }, [setActiveThreadId, cancelPending]);
};
